import { useMemo, useState } from 'react';
import { SiteDialog } from './SiteDialog';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { EmptyState } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { useSites, siteMutations } from '../../lib/queries';
import { SiteOrderPanel } from './SiteOrderPanel';
import { SITE_STATUS_LABELS } from '../../lib/labels';
import { formatDate } from '../../lib/dates';
import { ApiError } from '../../lib/api';
import type { SessionUser, Site, SiteStatus } from '../../lib/types';
import './baustellen.css';

const STATUS_ORDER: SiteStatus[] = ['aktiv', 'geplant', 'pausiert', 'abgeschlossen'];

export function BaustellenView({ user }: { user: SessionUser }) {
  const canEdit = user.role !== 'betrachter';
  const sites = useSites();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'alle' | SiteStatus>('alle');
  const [editing, setEditing] = useState<Site | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<Site | null>(null);
  const remove = siteMutations.useDelete();

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (sites.data ?? [])
      .filter((site) => statusFilter === 'alle' || site.status === statusFilter)
      .filter((site) =>
        term ? `${site.name} ${site.client} ${site.address}`.toLowerCase().includes(term) : true,
      )
      .sort(
        (a, b) =>
          STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) ||
          a.sortOrder - b.sortOrder ||
          a.name.localeCompare(b.name, 'de'),
      );
  }, [sites.data, search, statusFilter]);

  const activeCount = (sites.data ?? []).filter((site) => site.status === 'aktiv').length;

  if (sites.isLoading) {
    return (
      <div className="loading">
        <span className="spinner" aria-hidden="true" />
        Baustellen werden geladen …
      </div>
    );
  }

  return (
    <div className="view">
      <header className="view-head">
        <div className="view-title">
          <span className="label">Stammdaten</span>
          <h1>Baustellen</h1>
          <p className="view-lead">
            {activeCount} aktiv · {sites.data?.length ?? 0} insgesamt
          </p>
        </div>

        {canEdit ? (
          <button type="button" className="btn btn-signal" onClick={() => setEditing(null)}>
            Baustelle anlegen
          </button>
        ) : null}
      </header>

      <div className="filter-bar card" style={{ borderRadius: 'var(--radius)' }}>
        <input
          type="search"
          className="input filter-search"
          placeholder="Nach Bezeichnung oder Auftraggeber suchen …"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Baustellen suchen"
        />
        <div className="segmented" role="group" aria-label="Nach Status filtern">
          <button
            type="button"
            aria-pressed={statusFilter === 'alle'}
            onClick={() => setStatusFilter('alle')}
          >
            Alle
          </button>
          {STATUS_ORDER.map((status) => (
            <button
              key={status}
              type="button"
              aria-pressed={statusFilter === status}
              onClick={() => setStatusFilter(status)}
            >
              {SITE_STATUS_LABELS[status]}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="card">
          <EmptyState
            title="Keine Baustellen gefunden"
            description={
              (sites.data?.length ?? 0) === 0
                ? 'Ohne Baustellen kann im Schichtplan niemand eingeteilt werden. Lege die erste an.'
                : 'Für diese Filter gibt es keine Treffer.'
            }
            {...(canEdit && (sites.data?.length ?? 0) === 0
              ? {
                  action: (
                    <button type="button" className="btn btn-signal" onClick={() => setEditing(null)}>
                      Baustelle anlegen
                    </button>
                  ),
                }
              : {})}
          />
        </div>
      ) : (
        <>
          {canEdit ? <SiteOrderPanel sites={sites.data ?? []} /> : null}
          <div className="site-grid">
          {visible.map((site) => (
            <article
              key={site.id}
              className={`site-card status-${site.status}`}
              style={{ '--site': site.color } as never}
            >
              <div className="site-card-top">
                <span className={`badge badge-${site.status}`}>{SITE_STATUS_LABELS[site.status]}</span>
              </div>

              <h3 className="site-name">{site.name}</h3>

              <dl className="site-meta">
                {site.client ? (
                  <>
                    <dt>Auftraggeber</dt>
                    <dd>{site.client}</dd>
                  </>
                ) : null}
                {site.address ? (
                  <>
                    <dt>Adresse</dt>
                    <dd>{site.address}</dd>
                  </>
                ) : null}
                {site.startDate || site.endDate ? (
                  <>
                    <dt>Laufzeit</dt>
                    <dd className="numeric">
                      {site.startDate ? formatDate(site.startDate) : 'offen'} –{' '}
                      {site.endDate ? formatDate(site.endDate) : 'offen'}
                    </dd>
                  </>
                ) : null}
              </dl>

              {site.notes ? <p className="site-notes">{site.notes}</p> : null}

              {canEdit ? (
                <div className="site-card-actions">
                  <button type="button" className="btn btn-small" onClick={() => setEditing(site)}>
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    className="btn btn-small btn-danger"
                    onClick={() => setDeleting(site)}
                  >
                    Löschen
                  </button>
                </div>
              ) : null}
            </article>
          ))}
          </div>
        </>
      )}

      {editing !== undefined ? <SiteDialog site={editing} onClose={() => setEditing(undefined)} /> : null}

      {deleting ? (
        <ConfirmDialog
          title="Baustelle löschen"
          message={`Baustelle "${deleting.name}" wirklich löschen?`}
          detail="Vergangene Einteilungen bleiben zur Nachvollziehbarkeit erhalten, künftige werden entfernt. Soll die Baustelle nur ruhen, setze stattdessen den Status auf „Pausiert“ oder „Abgeschlossen“."
          confirmLabel="Löschen"
          busy={remove.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() =>
            remove.mutate(
              { id: deleting.id },
              {
                onSuccess: () => {
                  toast.success(`Baustelle "${deleting.name}" wurde gelöscht.`);
                  setDeleting(null);
                },
                onError: (error) => {
                  toast.error(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen.');
                  setDeleting(null);
                },
              },
            )
          }
        />
      ) : null}
    </div>
  );
}
